<!--
================================================
Un composant affichant les kpi des projets

Url d'accès :
http://localhost:8080/fr/adobe/projects/project-photoshop/details
================================================
-->


<!------------------------------------------------
+++++++++++++++++++++ HTML +++++++++++++++++++++++
-------------------------------------------------->

<template>
  <div id="ProjectKpiComp" v-bind:class="{'darkMode':themeToDisplay=='dark','demoMode':isDemoMode}" class="is-relative">
    <span class="demoWarning p-1 px-3 is-size-7 has-text-warning-dark has-text-weight-bold has-background-warning" v-if="isDemoMode">
                      <font-awesome-icon class="mr-1" :icon="['fas', 'exclamation-circle']" size="1x" />
{{$t('modeDémo')}}</span>

    <div v-if="project.durationDetailed"> <!-- v-if évite une erreur pendant que la prop n'est pas encore hydratée-->
      <div class="columns is-centered lineOne">

        <div class="column is-10-full-hd  budgetPart">

          <div class="columns is-centered budgetCont">

            <div class="column is-narrow">
              <h4>{{ $t('tempsEstimé') }}</h4>
              <div class="kpi estimate">
                <span v-if="project.estimate!=null">{{ project.estimate }} {{ $t('h') }}</span>
                <span v-else>0 {{ $t('h') }}</span>
              </div>
            </div>


            <div class="column is-narrow">
              <h4>{{ $t('tempsPassé') }}</h4>
              <div class="kpi spend">
                {{ formatDurationInHourAndMin(project.durationDetailed.all, $moment, this.locale) }}
              </div>
            </div>

            <div class="column is-narrow">
              <h4>{{ $t('budgetConsommé') }}</h4>
              <div class="kpi consumed">
                {{ calculateProjectPercent(project) }} %
                <font-awesome-icon class="icon question noPrint" :icon="['far', 'question-circle']" id="tooltip-percent"
                                   :content="$t('tooltip-représenteLePourcentageDuTempsPasséParRapportAuTempsEstimé')"
                                   v-tippy="{theme:themeToDisplay=='dark' ? 'cronotipdark' : 'cronotip'}"
                                   @shown="trackTooltip('[project-details] > percent')"
                />
              </div>
            </div>

            <div class="column is-narrow">
              <h4>{{ $t('tempsRestant') }}</h4>
              <div class="kpi remain">
                {{ formatDurationInHourAndMin(project.remainingTime, $moment, this.locale) }}
              </div>
            </div>

          </div>
          <div class="columns is-centered progressCont">

            <div class="column is-12-touch is-8 is-7-fullhd">
              <project-progressionbar v-bind:project="project"></project-progressionbar>
            </div>
          </div>


        </div>
      </div>

      <div class="columns is-centered lineTwo ">
        <font-awesome-icon class="mainIcon " :icon="['fas', 'dollar-sign']"/>

        <div class="column kpiCont billableTime">
          <h4>{{ $t('TempsFacturable') }}</h4>
          <div class="kpi" v-if="project.durationDetailed">
            {{ formatDurationInHourAndMin(project.durationDetailed.billable, $moment, this.locale) }}
            <span class="cost ">
                 <span v-if="project.cost.billable!=0">
                  {{ project.cost.billable }} {{ currencyToSign(project.cost.currency) }}
                 </span>
                 <span v-else>0 {{ currencyToSign($auth.getActiveWorkspaceCurrency()) }} </span>
                <font-awesome-icon class="icon question noPrint" :icon="['far', 'question-circle']"
                                   id="tooltip-billableTime"
                                   :content="$t ('tooltip-calculéDapresLeTauxHoraireDesAuteuresDesPointagesFacturables')"
                                   v-tippy="{theme:themeToDisplay=='dark' ? 'cronotipdark' : 'cronotip'}"
                                   @shown="trackTooltip('[project-details] > billableTime')"
                />
              </span>
          </div>
        </div>

        <div class="column kpiCont nonBillableTime">
          <h4>{{ $t('TempsNonFacturable') }}</h4>
          <div class="kpi">
            {{ formatDurationInHourAndMin(project.durationDetailed.nonBillable, $moment, this.locale) }}
            <span class="cost ">

                 <span v-if="project.cost.nonBillable!=0">
                  {{ project.cost.nonBillable }} {{ currencyToSign(project.cost.currency) }}
                 </span>
                 <span v-else>0 {{ currencyToSign($auth.getActiveWorkspaceCurrency()) }} </span>
                  <font-awesome-icon class="icon question noPrint" :icon="['far', 'question-circle']"
                                     id="tooltip-nonBillableTime"
                                     :content="$t('tooltip-calculéDapresLeTauxHoraireDesAuteuresDesPointagesNonFacturables')"
                                     v-tippy="{theme:themeToDisplay=='dark' ? 'cronotipdark' : 'cronotip'}"
                                     @shown="trackTooltip('[project-details] > nonBillableTime')"
                  />
              </span>
          </div>
        </div>

        <div class="column kpiCont totalTime">
          <h4>{{ $t('FacturablePlusNonFacturable') }}</h4>
          <div class="kpi">
            {{ formatDurationInHourAndMin(project.durationDetailed.all, $moment, this.locale) }}

            <span class="cost">
              <span v-if="project.cost.nonBillable!=0 && project.cost.billable!=0">
                {{ project.cost.billable + project.cost.nonBillable }}
                {{ currencyToSign(project.cost.currency) }}
                 </span>
              <span v-else>0 {{ currencyToSign($auth.getActiveWorkspaceCurrency()) }} </span>              </span>


          </div>
        </div>


      </div>
    </div>

  </div>

  <!--    https://www.npmjs.com/package/v-click-outside-->
  <!--    <div v-click-outside="onClickOutside"></div> -->
</template>

<!------------------------------------------------
+++++++++++++++++++ JAVASCRIPT++++++++++++++++++++
-------------------------------------------------->


<script>
import {mapState} from 'vuex'
import SentryMixin from "@/mixins/SentryMixin.js";
import GlobalErrorNotification from "@/mixins/GlobalErrorNotificationMixin.js";
import InterceptorMixin from "@/mixins/InterceptorMixin.js";
import TrackingMixin from "@/mixins/TrackingMixin";
import ProjectProgressionbar from "@/components/ProjectProgressBar";
import ProjectMixin from "@/mixins/ProjectMixin";
import TimeMixin from "@/mixins/TimeMixin";
import CurrencyMixin from "@/mixins/CurrencyMixin";

export default {
  name: 'ProjectKpiComp',
  components: {ProjectProgressionbar},
  //utile dans vue dev tool
  mixins: [
    GlobalErrorNotification,
    SentryMixin,
    InterceptorMixin,
    TrackingMixin,
    ProjectMixin,
    TimeMixin,
    CurrencyMixin
  ],
  // components: {},

  props: {
    /** pour typage et validation voir :
     *  https://fr.vuejs.org/v2/guide/components-props.html#Types-des-props
     *  https://fr.vuejs.org/v2/guide/components-props.html#Validation-des-props
     **/
    //
    project: {
      type: Object, // l'objet project
      required: true,
      //   default: false,
      //  ou si l'on veut que défaut soit une  fonction ex : pour retourner un objet vide
      default: () => ({})
    },
    isDemoMode: { // un boolean indiquant s'il faut afficher un projet de démonstration dans le composant au lieu de project.
      type:Boolean,
      required: false, default: false,
    }
  },
  data: function () {
    return {
      locale: this.$route.params.locale,
      // pour les composants où l'user est identifié ///////////////////////
      // organizationSlug: this.$route.params.organization
      // userSlug: JSON.parse(this.$cookie.get('auth')).userSlug (ou dispo dans this.$auth.getUserSlug())
      componentIsReady: false, // indique quand le composant est prêt à être affiché
      // =================================================================
    }
  },

  computed: {
    // computed props for store :
    ...mapState({
      user: state => state.user,
      themeToDisplay: state => state.app.themeToDisplay
    }),


  },

  methods: {},

  // LIFECYCLE HOOKS (https://v2.vuejs.org/v2/guide/instance.html#Lifecycle-Diagram)
  mounted: function () {

  },
  beforeDestroy() {
    /**
     * Lorsqu'un event semble être déclenché plusieurs fois (alors qu'il ne devrait pas) il peut-être nécéssaire de supprimer son listener...
     Cette solution est inspirée de
     - https://stackoverflow.com/a/61049328/666416
     - https://charlietheprogrammer.com/duplicate-vue-event-listeners-and-how-i-solved-them
     **/
    // this.$root.$off('timerecord_suggestions_required', () => {
    //   console.debug('suppression du event listener timerecord_suggestions_required')
    // })

  },
}
</script>

<!------------------------------------------------
++++++++++++++++++++++ CSS +++++++++++++++++++++++
-------------------------------------------------->

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
@import "../assets/css/globalVar.scss";
/*@import "../assets/css/bulmaResponsiveDep.scss";*/

#ProjectKpiComp {

  //.column {border: dashed 1px #eee}
  //.columns {border: dashed 1px #ddd}
  //////////////////////////////////////////
  // LIGHT THEME ///////////////////////////
  background-color: #fff;
  padding: 3rem;
  border-radius: .3rem;
  color: #666;
  box-shadow: 0 0 5px #ccc;

  &.demoMode{
    border: dashed 1px $brandFourth;
    .demoWarning{
      background-color: $brandFourth !important;
      color:white !important;
      border-radius: 1px;
      border:none !important;
      position: absolute;
      //margin: auto;
      left:-1px;
      top: -1px;
    }
  }

  h4 {
    color: #949494;
    font-weight: bold;
    font-size: .95rem;
  }

  .kpi {
    color: $brandThird;
    font-size: 2rem;
    font-weight: bold;
    display: flex;
    justify-content: center;
  }

  .lineOne {
    border-bottom: solid 2px;
    border-color: #eee;


    .budgetPart {

      .budgetCont {
        margin-bottom: 0;

      }

      .column {
        padding-right: 1.2rem; padding-left: 1.2rem;
      }

      .progressCont {
        margin: auto;

        .column {
          padding-top: 0; // pour coller la progress bar sur .budgetCont
        }
      }
    }
  }

  .lineTwo {
    margin-top: 1.5rem;

    .mainIcon {
      color: #eee;
      margin-top: 1.5rem;
      height: 3rem;
      width: 3rem;
    }

    .icon {

    }

    .kpiCont {


      .cost {
        margin-left: 1rem;
      }

      &.billableTime {
        .cost {
          color: $brandPrimary;
        }

      }

      &.nonBillableTime, &.totalTime {
        .cost {
          color: $brandFourth;
        }

      }
    }

  }

  .icon.question {
    color: #ccc;
    height: 1.2rem;
    width: 1.2rem;
    margin-left: .4rem;
    align-self: center;
  }

  /////////////////////////////////////////
  // DARK THEME ///////////////////////////
  &.darkMode {


    &.demoMode{
      .demoWarning{
        border: dashed 1px $darkYellow;
      }
    }
    box-shadow: none;

    pre {
      background-color: #111;
      color: #ccc;
    }

    h4 {

    }

    .kpi {
      color: $darkContrastTxt;
    }

    background-color: $darkBg2;
    color: $darkTxt;

    .lineOne {
      border-bottom-color: $darkBg;

    }

    .lineTwo {


      .mainIcon {
        color: $darkBg;
      }

      .kpiCont {

        &.billableTime {
        }

        &.nonBillableTime, &.totalTime {
          .cost {
            color: $brandFourth;
          }

        }
      }

    }

    .icon.question {
      color: lighten($darkBg, 10%);
    }

  }

}
</style>
